import { InternalStatusCode } from '../../network/httpClients/OIDCAuthenticationClient/api.schema';
import { getDynamicSetting } from '../../utils/dynamicSettings';

export const MIXPANEL_EVENT_NAMES = {
  SYSTEM: 'System',
  ACTION: 'Action',
  VIEW: 'View',
  RESPONSE: 'Response',
  ERROR: 'Error',
} as const;

export const MIXPANEL_OPERATIONS = {
  PAGE_VIEW: 'page_view',
  SELECT_IDE_MARKETPLACE: 'select_ide_marketplace',
  SELECT_SIGNUP_METHOD: 'select_sign_up_method',
  CHOOSE_DIFFERENT_SIGNUP_METHOD: 'choose_different_signup_method',
  SUBMIT_EMAIL: 'submit_email',
  SUBMIT_SSO: 'submit_sso',
  REQUEST_EMAIL_RESEND: 'request_email_resend',
  CONTACT_SUPPORT: 'contact_support',
  LOGIN_INTERRUPTED: 'login_interrupted',
  PORTAL_LOGIN_SUCCESS: 'portal_login_success',
  // CLI/IDE device-code validation step (RFC 8628): the user confirms the code
  // shown in their terminal. Part of the one login flow shared with the CLI —
  // keyed on the same trace_id/login_session_id/init_client the wrapper attaches.
  DEVICE_CODE_VALIDATION_SUCCESS: 'device_code_validation_success',
  DEVICE_CODE_VALIDATION_FAILED: 'device_code_validation_failed',
  MODAL_VIEWED: 'modal_viewed',
  MODAL_CLICKED: 'modal_clicked',
  EMPTY_STATE_VIEWED: 'empty_state_viewed',
  EMPTY_STATE_CTA_CLICKED: 'empty_state_cta_clicked',
  GENERATE_RULE_CLICKED: 'generate_rule_clicked',
  GENERATE_RULE_SUBMITTED: 'generate_rule_submitted',
  GENERATED_RULE_ACCEPTED: 'generated_rule_accepted',
  GENERATED_RULE_SAVED_AS_SUGGESTION: 'generated_rule_saved_as_suggestion',
  GENERATED_RULE_ABANDONED: 'generated_rule_abandoned',
  RULE_SUGGESTION_VIEWED: 'rule_suggestion_viewed',
  RULE_SUGGESTION_ACCEPTED: 'rule_suggestion_accepted',
  RULE_SUGGESTION_REJECTED: 'rule_suggestion_rejected',
  RULE_VIEWED: 'rule_viewed',
  RULE_STATUS_CHANGED: 'rule_status_changed',
  RULE_DELETED: 'rule_deleted',
  RULE_EDIT_STARTED: 'rule_edit_started',
  RULE_EDIT_SAVED: 'rule_edit_saved',
  RULES_EXPORT_CSV_CLICKED: 'rules_export_csv_clicked',
  REVIEW_STANDARDS_TAB_VIEWED: 'review_standards_tab_viewed',
  SKILL_STATUS_CHANGED: 'skill_status_changed',
  SKILL_VIEWED: 'skill_viewed',
  SKILL_EDIT_STARTED: 'skill_edit_started',
  SKILL_EDIT_SAVED: 'skill_edit_saved',
  FINDING_CLICKED: 'finding_clicked',
  BILLING_DATE_RANGE_CHANGED: 'billing_date_range_changed',
  BILLING_TAB_CHANGED: 'billing_tab_changed',
  BILLING_METRIC_CHANGED: 'billing_metric_changed',
  BILLING_SEARCH_APPLIED: 'billing_search_applied',
  BILLING_LOAD_MORE_CLICKED: 'billing_load_more_clicked',
  BILLING_CONTACT_SALES_CLICKED: 'billing_contact_sales_clicked',
  BILLING_READ_MORE_CLICKED: 'billing_read_more_clicked',
  BILLING_UPGRADE_PLAN_CLICKED: 'billing_upgrade_plan_clicked',
  // Conversion popup funnel (PLT-4031).
  CONVERSION_POPUP_VIEWED: 'conversion_popup_viewed',
  CONVERSION_POPUP_CHECKOUT_CLICKED: 'conversion_popup_checkout_clicked',
  CONVERSION_POPUP_LARGER_TEAMS_CLICKED:
    'conversion_popup_larger_teams_clicked',
  CONVERSION_POPUP_DISMISSED: 'conversion_popup_dismissed',
  CHECKOUT_RETURN_SUCCESS: 'checkout_return_success',
  CHECKOUT_RETURN_CANCELED: 'checkout_return_canceled',
  // "New usage-based model" migration banner (PLT-4697).
  USAGE_MODEL_BANNER_SHOWN: 'usage_model_banner_shown',
  USAGE_MODEL_BANNER_DISMISSED: 'usage_model_banner_dismissed',
  // Free-to-Trial migration plan dialogs (intro popup + plan chooser). The
  // chooser's start-plan and dismiss are already covered by the conversion-popup
  // funnel above, so only the net-new interactions get their own operations.
  SELECT_A_PLAN_CLICKED: 'select_a_plan_clicked',
  PLAN_INTRO_CONTACT_SUPPORT_CLICKED: 'plan_intro_contact_support_clicked',
  PLAN_CAROUSEL_CLICKED: 'plan_carousel_clicked',
  PLAN_CHECKBOX_CLICKED: 'plan_checkbox_clicked',
  // Reserved: no settings control exists in the plan dialogs yet, so this is
  // intentionally unwired until one ships.
  PLAN_SETTINGS_CLICKED: 'plan_settings_clicked',
  PLAN_CHOOSER_CONTACT_SUPPORT_CLICKED: 'plan_chooser_contact_support_clicked',
  FINDINGS_EXPORT_CSV_CLICKED: 'findings_export_csv_clicked',
  ONBOARDING_WIZARD_STEP_VIEWED: 'onboarding_wizard_step_viewed',
  ONBOARDING_WIZARD_STEP_ACTION_CLICKED:
    'onboarding_wizard_step_action_clicked',
  ONBOARDING_WIZARD_COMPLETED: 'onboarding_wizard_completed',
  // Team Profiles (Developer Personas) feature-usage funnel.
  PERSONA_CATALOG_VIEWED: 'persona_catalog_viewed',
  PERSONA_CATALOG_EMPTY_STATE_VIEWED: 'persona_catalog_empty_state_viewed',
  PERSONA_FILTER_APPLIED: 'persona_filter_applied',
  PERSONA_DETAIL_VIEWED: 'persona_detail_viewed',
  PERSONA_REFRESH_CLICKED: 'persona_refresh_clicked',
  PERSONA_PROMPT_EDIT_STARTED: 'persona_prompt_edit_started',
  PERSONA_PROMPT_EDIT_SAVED: 'persona_prompt_edit_saved',
  PERSONA_VERSION_ROLLBACK_CLICKED: 'persona_version_rollback_clicked',
  PERSONA_SETTINGS_OPENED: 'persona_settings_opened',
  PERSONA_SETTINGS_SAVED: 'persona_settings_saved',
  PERSONA_GENERATE_INITIAL_CLICKED: 'persona_generate_initial_clicked',
  // Auto-approve/block PRs (merge automation) Research Preview feature.
  MERGE_AUTOMATION_SETTINGS_SAVED: 'merge_automation_settings_saved',
  MERGE_AUTOMATION_STATUS_CHANGED: 'merge_automation_status_changed',
  REPOSITORIES_TAB_VIEWED: 'repositories_tab_viewed',
  REPOSITORIES_RELATIONSHIPS_VIEW_CHANGED:
    'repositories_relationships_view_changed',
  INSTALLATION_SELECTED: 'installation_selected',
  INSTALLATION_DELETION_INITIATED: 'installation_deletion_initiated',
  INSTALLATION_DELETED: 'installation_deleted',
  WEBHOOKS_REINSTALL_INITIATED: 'webhooks_reinstall_initiated',
  REPOSITORY_VIEWED: 'repository_viewed',
  RELATED_REPO_ADDED: 'related_repo_added',
  RELATED_REPO_REMOVED: 'related_repo_removed',
  RELATED_REPO_RETRY_INITIATED: 'related_repo_retry_initiated',
  RELATED_REPO_MARKDOWN_DOWNLOADED: 'related_repo_markdown_downloaded',
  RELATED_REPO_MARKDOWN_VIEWED: 'related_repo_markdown_viewed',
  RELATED_REPO_RETRY_SUBMITTED: 'related_repo_retry_submitted',
  RELATED_REPO_PROCESSED: 'related_repo_processed',
  REPOSITORIES_RELATIONSHIPS_FILTERED: 'repositories_relationships_filtered',
  AUTO_DISCOVER_CLICKED: 'auto_discover_clicked',
  AUTO_DISCOVER_COMPLETED: 'auto_discover_completed',
  // Manual PR-history indexing trigger, offered from both the repositories
  // table row and the side panel.
  PR_KNOWLEDGE_INDEX_INITIATED: 'pr_knowledge_index_initiated',
  // Configuration page UI analytics (IDE-4177).
  CONFIGURATIONS_TAB_VIEWED: 'configurations_tab_viewed',
  // `_changed` fires on every staged edit; `_updated` fires once per setting
  // that is actually persisted on Save (same payload, plus `save_id`).
  CONFIGURATIONS_SETTING_CHANGED: 'configurations_setting_changed',
  CONFIGURATIONS_SETTING_UPDATED: 'configurations_setting_updated',
  CONFIGURATIONS_RESET_INITIATED: 'configurations_reset_initiated',
  CONFIGURATIONS_RESET_CONFIRMED: 'configurations_reset_confirmed',
  CONFIGURATIONS_LIVE_PREVIEW_INTERACTED:
    'configurations_live_preview_interacted',
  // Bulk operations on the Review Standards page. Each action emits a pair:
  // `_initiated` on trigger, `_completed` on the backend outcome (success and
  // failure), joinable on action + selected_count.
  RULE_BULK_ACTION_INITIATED: 'rule_bulk_action_initiated',
  RULE_BULK_ACTION_COMPLETED: 'rule_bulk_action_completed',
  RULE_SUGGESTION_BULK_ACTION_INITIATED:
    'rule_suggestion_bulk_action_initiated',
  RULE_SUGGESTION_BULK_ACTION_COMPLETED:
    'rule_suggestion_bulk_action_completed',
  SKILLS_BULK_ACTION_INITIATED: 'skills_bulk_action_initiated',
  SKILLS_BULK_ACTION_COMPLETED: 'skills_bulk_action_completed',
  REPOSITORY_BULK_ACTION_INITIATED: 'repository_bulk_action_initiated',
  REPOSITORY_BULK_ACTION_COMPLETED: 'repository_bulk_action_completed',
  // Sandbox configuration page actions.
  SANDBOX_CONFIG_SAVED: 'sandbox_config_saved',
  SANDBOX_CONFIG_DELETED: 'sandbox_config_deleted',
  SANDBOX_CONFIG_REPO_NAME_APPLIED: 'sandbox_config_repo_name_applied',
  // Agentic Toolbox install command, sliced by `install_platform`.
  TOOLBOX_INSTALL_COMMAND_COPIED: 'toolbox_install_command_copied',
} as const;

/**
 * Entry points that can open the conversion popup, surfaced as the
 * `trigger_source` on CONVERSION_POPUP_VIEWED (PLT-4031) so DS can slice the
 * funnel by where the upgrade intent originated.
 */
export const CONVERSION_POPUP_TRIGGER_SOURCES = {
  BILLING_PAGE: 'billing_page',
  SIDEBAR_WIDGET: 'sidebar_widget',
  STATUS_PILL: 'status_pill',
  TRIAL_END_BLOCK: 'trial_end_block',
  HOME_CARD: 'home_card',
  MIGRATION_WELCOME_POPUP: 'migration_welcome_popup',
} as const;

/**
 * How the user dismissed the conversion popup, surfaced as `dismiss_method` on
 * CONVERSION_POPUP_DISMISSED (PLT-4031).
 */
export const CONVERSION_POPUP_DISMISS_METHODS = {
  CLOSE_X: 'close_x',
  BACKDROP: 'backdrop',
  ESC: 'esc',
} as const;

/** Which control dismissed the usage-model migration banner (PLT-4697). */
export const USAGE_MODEL_BANNER_DISMISS_METHODS = {
  CLOSE_X: 'close_x',
  OK_BUTTON: 'ok_button',
} as const;

export const EXPECTED_ERROR_INTERNAL_STATUS_CODES: InternalStatusCode[] = [
  'AUTH_INVITE_REQUIRED',
  'AUTH_SSO_LOGIN_REQUIRED',
  'SSO_NOT_CONFIGURED_FOR_ORGANIZATION',
  'INVALID_EMAIL',
  'AUTH_USER_DISABLED',
  'INVALID_OR_EXPIRED_EMAIL_LINK',
  'AUTH_DOMAIN_BLOCKED',
  'AUTH_EMAIL_BLOCKED',
];

export const getMixpanelApiHost = () => getDynamicSetting('mixpanel_api_host');

export const PROPERTIES = {
  BROWSER_UNIQUE_ID: 'browser_unique_id',
  LOGIN_SESSION_ID: 'login_session_id',
  TRACE_ID: 'trace_id',
  INIT_CLIENT: 'init_client',
  AUTH_PROTOCOL: 'auth_protocol',
} as const;
